import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { exportOrder } from "@/lib/exportOrder";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, Download, FileText, ChevronRight, Phone, Trophy } from "lucide-react";

const NAVY = "#0F2C59";
const MEDALS = ["#D4AF37", "#9CA3AF", "#B45309"];

export const VotingPower = ({ committeeNo = "all" }) => {
  const [data, setData] = useState(null);
  const [committee, setCommittee] = useState(committeeNo || "all");
  useEffect(() => { setCommittee(committeeNo || "all"); }, [committeeNo]);
  const [q, setQ] = useState("");
  const [only, setOnly] = useState("all");
  const [open, setOpen] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    setData(null);
    const params = committee !== "all" ? { committee_no: Number(committee) } : {};
    api.get("/reports/voting-power", { params })
      .then(({ data }) => setData(data))
      .catch(() => { setData({ rows: [], committees: [] }); toast.error("Sıralama yüklenemedi"); });
  }, [committee]);

  const rows = useMemo(() => {
    if (!data) return [];
    const term = q.trim().toLocaleLowerCase("tr");
    return (data.rows || []).filter((r) => {
      if (only === "multi" && r.votes < 2) return false;
      if (only === "cross" && r.committee_count < 2) return false;
      if (!term) return true;
      return (r.person || "").toLocaleLowerCase("tr").includes(term)
        || r.firms.some((f) => (f.company_name || "").toLocaleLowerCase("tr").includes(term));
    });
  }, [data, q, only]);

  const doExport = async (fmt) => {
    setExporting(true);
    try {
      const params = { format: fmt, only: 1, order: await exportOrder(rows.map((r) => r.id)),
        ...(committee !== "all" ? { committee_no: Number(committee) } : {}) };
      const res = await api.get("/reports/voting-power/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `oy_gucu_siralamasi.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch {
      toast.error("İndirilemedi");
    } finally {
      setExporting(false);
    }
  };

  if (!data) return <Skeleton className="h-80 w-full" data-testid="vpow-loading" />;

  const maxVotes = rows[0]?.votes || 1;

  return (
    <div className="space-y-3" data-testid="voting-power">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { l: "Oy Kullanacak Kişi", v: data.total_persons, c: NAVY },
          { l: "Toplam Oy", v: data.total_votes, c: "#16A34A" },
          { l: "Yetki Belgeli", v: data.belge_votes, c: "#0EA5E9" },
          { l: "Şahıs (kimlikle)", v: data.sahis_votes, c: "#7C3AED" },
          { l: "Çok Oylu Kişi", v: data.multi_count, c: "#F59E0B" },
        ].map((k) => (
          <Card key={k.l} className="p-3" data-testid={`vpow-kpi-${k.l}`}>
            <div className="text-xl font-bold tabular-nums" style={{ color: k.c }}>{k.v}</div>
            <div className="text-xs text-muted-foreground">{k.l}</div>
          </Card>
        ))}
      </div>

      {!!data.missing_person && (
        <div className="text-xs rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 px-3 py-2 text-amber-800 dark:text-amber-300" data-testid="vpow-missing-note">
          {data.missing_person} firmada oy kullanacak yetkili henüz girilmediği için sıralamaya dahil
          edilemedi. “Oy Kullanacak Yetkililer” sekmesinden tamamlayabilirsiniz.
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Select value={String(committee)} onValueChange={setCommittee}>
          <SelectTrigger className="h-9 w-56" data-testid="vpow-committee"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">Tüm komiteler</SelectItem>
            {(data.committees || []).map((c) => (
              <SelectItem key={c.committee_no} value={String(c.committee_no)}>{c.committee_no}. {c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={only} onValueChange={setOnly}>
          <SelectTrigger className="h-9 w-56" data-testid="vpow-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tümü</SelectItem>
            <SelectItem value="multi">Yalnız 2+ oy kullananlar</SelectItem>
            <SelectItem value="cross">Birden fazla komitede oy kullananlar</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Kişi veya firma ara..." value={q}
            onChange={(e) => setQ(e.target.value)} data-testid="vpow-search" />
        </div>
        <Button variant="outline" size="sm" className="h-9" disabled={exporting || !rows.length}
          onClick={() => doExport("xlsx")} data-testid="vpow-export-xlsx">
          <Download className="h-4 w-4 mr-1.5" />Excel
        </Button>
        <Button variant="outline" size="sm" className="h-9" disabled={exporting || !rows.length}
          onClick={() => doExport("pdf")} data-testid="vpow-export-pdf">
          <FileText className="h-4 w-4 mr-1.5" />PDF
        </Button>
      </div>

      <p className="text-xs text-muted-foreground" data-testid="vpow-count">
        <b>{rows.length}</b> kişi · toplam <b>{rows.reduce((s, r) => s + r.votes, 0)}</b> oy
        {committee !== "all" ? " (seçili komite)" : ""}
      </p>

      <Card className="divide-y divide-border/60" data-testid="vpow-list">
        {rows.map((r, i) => (
          <div key={r.id} data-testid={`vpow-row-${r.id}`}>
            <button type="button" onClick={() => setOpen(open === r.id ? null : r.id)}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-muted/30 transition-colors"
              data-testid={`vpow-toggle-${r.id}`}>
              <span className="w-8 shrink-0 text-sm font-bold tabular-nums text-center"
                style={{ color: i < 3 ? MEDALS[i] : undefined }}>
                {i < 3 ? <Trophy className="h-4 w-4 mx-auto" style={{ color: MEDALS[i] }} /> : r.rank}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold truncate">{r.person}</span>
                <span className="mt-1 block h-1.5 rounded-full bg-muted overflow-hidden max-w-xs">
                  <span className="block h-full rounded-full" style={{ width: `${Math.max((r.votes / maxVotes) * 100, 6)}%`, background: NAVY }} />
                </span>
              </span>
              <span className="hidden sm:flex items-center gap-1.5 shrink-0">
                {r.belge > 0 && <Badge variant="outline" className="font-normal text-[10px]" style={{ color: "#0EA5E9", borderColor: "#0EA5E9" }}>{r.belge} belge</Badge>}
                {r.sahis > 0 && <Badge variant="outline" className="font-normal text-[10px]" style={{ color: "#7C3AED", borderColor: "#7C3AED" }}>{r.sahis} şahıs</Badge>}
                <Badge variant="outline" className="font-normal text-[10px]">{r.committee_count} komite</Badge>
              </span>
              <span className="w-16 shrink-0 text-right">
                <span className="text-lg font-bold tabular-nums" style={{ color: NAVY }}>{r.votes}</span>
                <span className="text-[10px] text-muted-foreground block leading-none">oy</span>
              </span>
              <ChevronRight className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open === r.id ? "rotate-90" : ""}`} />
            </button>
            {open === r.id && (
              <div className="px-3 pb-3 bg-muted/20" data-testid={`vpow-detail-${r.id}`}>
                {r.phone && (
                  <a href={`tel:${r.phone}`} className="inline-flex items-center gap-1 text-xs text-sky-700 dark:text-sky-400 hover:underline mb-2">
                    <Phone className="h-3 w-3" />{r.phone}
                  </a>
                )}
                <div className="overflow-x-auto rounded-md border border-border bg-background">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/60 text-muted-foreground">
                      <tr>
                        <th className="text-left font-medium py-1.5 px-2">Firma</th>
                        <th className="text-left font-medium py-1.5 px-2">Sicil</th>
                        <th className="text-left font-medium py-1.5 px-2">Komite</th>
                        <th className="text-left font-medium py-1.5 px-2">Tür</th>
                        <th className="text-left font-medium py-1.5 px-2">Belge Kimde</th>
                      </tr>
                    </thead>
                    <tbody>
                      {r.firms.map((f) => (
                        <tr key={f.id} className="border-t border-border/40">
                          <td className="py-1.5 px-2 font-medium">{f.company_name}</td>
                          <td className="py-1.5 px-2 text-muted-foreground">{f.sicil_no || "—"}</td>
                          <td className="py-1.5 px-2">{f.committee_no ? `#${f.committee_no} ${f.committee_name}` : "—"}</td>
                          <td className="py-1.5 px-2">
                            {f.sahis
                              ? <span className="text-violet-700 dark:text-violet-300">Şahıs</span>
                              : <span className="text-sky-700 dark:text-sky-300">Yetki belgeli</span>}
                          </td>
                          <td className="py-1.5 px-2 text-muted-foreground">{f.sahis ? "—" : (f.document_holder || "girilmedi")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ))}
        {rows.length === 0 && <p className="p-8 text-center text-sm text-muted-foreground" data-testid="vpow-empty">Kayıt bulunamadı</p>}
      </Card>

      <p className="text-xs text-muted-foreground">
        Sıralama, yetki belgesi alınmış tüzel firmalarda <b>oy kullanacak yetkili</b> ile şahıs
        firmalarında kimlikle oy kullanacak kişiyi birleştirir; yalnız FAAL firmalar sayılır.
        Aynı kişinin farklı yazımları (büyük/küçük harf, Türkçe karakter) tek kişide toplanır.
      </p>
    </div>
  );
};
